import { isCropHidden } from "@omnia/shared/crops"
import { addNutrients, getRequirementsLeft, getRequirementsNutrients } from "../../../nutrients"
import { FieldViewModel, INMaxCropGroup, INMaxCropping } from "../../../omnia"
import { BalanceProvider } from "../../../services/balance"
import { getNMaxAdjustments } from "../../../services/nmax"
import { INtrBalance, INtrNutrients, INtrReq, INutrient, NutrientGroup } from "../../../types"
import { CroppingReqVm } from "../../cropping-req-vm"
import { AppVm } from "../app"
import { PlanReader } from "../plan-reader"
import { ICroppingPlanVm } from "../types"

export class CroppingVm extends CroppingReqVm {

    public static create(cropping: FieldViewModel, nmaxGroup: INMaxCropGroup, reader: PlanReader): CroppingVm {

        const croppingId = cropping.croppingId;

        const req = reader.getCroppingInitialRequirements(croppingId);

        const plans = reader.getCroppingPlans(croppingId);

        const plansVm = AppVm.createViewModels(plans);

        const croppingVm = new CroppingVm(cropping, nmaxGroup, req, plansVm, reader.balance);

        croppingVm.initialZones = reader.getInitialRequirements(croppingId, true);
        croppingVm.closingZones = reader.getClosingRequirements(croppingId, true);

        return croppingVm;

    }


    public selected: boolean;

    public contrib: INtrNutrients;
    public closingReq: INtrNutrients;

    public readonly openingBalance: INtrBalance
    private _openingBalanceZones: INtrBalance[]

    public initialZones: INtrReq[];
    public closingZones: INtrReq[];

    public nmaxAdjustments = getNMaxAdjustments()
    public nmaxCropping: INMaxCropping;

    constructor(
        public croppingVm: FieldViewModel,
        public nmaxGroup: INMaxCropGroup,
        requirements: INtrReq,
        public readonly plans: ICroppingPlanVm[],
        private readonly balance: BalanceProvider,
        public readonly visiblePlans = plans.filter(p => p.visible),
        public readonly field = croppingVm.field,
        public readonly cropping = croppingVm.cropping,
        public readonly croppingId = croppingVm.croppingId,
        public readonly crop = croppingVm.crop,
        public readonly description = croppingVm.description,
        public readonly cssColor = croppingVm.cssColor,
        public readonly area = croppingVm.area,
        public readonly authorized = croppingVm.authorized
    ) {

        super(requirements);

        this.openingBalance = balance.getWholeField(croppingId)

        this.contrib = addNutrients(plans.map(c => c.plan.contrib).filter(c => !!c));

        let planReqs = getRequirementsNutrients(requirements);

        this.closingReq = getRequirementsLeft(planReqs, this.contrib);

        if (nmaxGroup && nmaxGroup.Cropping) {
            this.nmaxCropping = nmaxGroup.Cropping.find(c => c.CroppingId == this.croppingId)
        }

    }

    get openingBalanceZones() {
        const { croppingId, balance } = this
        return this._openingBalanceZones ?? (this._openingBalanceZones = balance.getZones(croppingId))
    }

    get shortDescription(): string {
        return this.croppingVm.getCroppingDescription(true, false, false);
    }

    get uncropped(): boolean {
        return (!this.crop) || (!this.crop.CropId);
    }

    get visible() {
        const c = this.croppingVm
        return c && c.currentYear && c.visible && c.fieldVm.visible && (!isCropHidden(c))
    }

    get hasRequirements(): boolean {
        return !!this.requirements;
    }

    findApplication(appId: number) {
        return this.plans.map(p => p.applications.find(a => (a.app.appId == appId))).find(a => !!a);
    }

    hasMultipleZones(nutrient: INutrient, closing: number): boolean {

        let zones: { NutrientGroup: NutrientGroup }[]

        switch (closing) {
            case - 1:
                zones = this.openingBalanceZones
                break;

            case 1:
                zones = this.closingZones
                break;

            default:
                zones = this.initialZones
                break;
        }

        let mask = Math.pow(2, nutrient.nutrient - 1);
        return zones.filter(z => z.NutrientGroup & mask).length > 1;
    }

}
